import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import TestAgentModal from '../Components/TestAgentModal';

import { ConfirmationProvider } from '../Components/ConfirmationContext';
import DateRangeSelector, { getDateRangeByPeriod } from '../Components/DateRangeSelector';
import Table from '../Components/Tables';
import { AgentApiProvider, useAgentApi } from '../Services/AgentApiProvider';
import { AssistantAuditOperationApi } from '../Services/AgentApiProviderTypes';
import { PromptApi } from '../Services/PromptApiProviderTypes';
import { AuditOperation } from '../Services/TaskScheduleApiProviderTypes';
import { formatDateUTC } from '../utils/formatDate';

function AgentAuditOperationsListContent() {
  const navigate = useNavigate();
  const { organizationId, agentId } = useParams();

  const { getAgentById, getAgentAuditOperations, getPrompts } = useAgentApi();

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // ── Agent info (for header & test panel) ──────────────────────────────
  const [name, setName] = useState<string>('');
  const [promptId, setPromptId] = useState<string>('');
  const [prompts, setPrompts] = useState<PromptApi[]>([]);
  const [isPromptRealtime, setIsPromptRealtime] = useState<boolean>(false);

  // ── Audit operations state ────────────────────────────────────────────
  const [detailAudit, setDetailAudit] = useState<AuditOperation | null>(null);
  const [auditOperations, setAuditOperations] = useState<AssistantAuditOperationApi[]>([]);
  const [totalAuditOperations, setTotalAuditOperations] = useState<number>(0);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);

  const [operationId, setOperationId] = useState<string>('');
  const [threadId, setThreadId] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  const columns = [
    { name: 'ID', selector: (row: AssistantAuditOperationApi) => row.id, sortable: true },
    {
      name: 'Thread ID',
      selector: (row: AssistantAuditOperationApi) => row.thread_id,
      sortable: true,
    },
    {
      name: 'Duration',
      selector: (row: AssistantAuditOperationApi) => row.duration,
      sortable: true,
    },
    {
      name: 'Created',
      selector: (row: AssistantAuditOperationApi) => formatDateUTC(row.created_at),
      sortable: true,
    },
  ];

  // ── Prompt realtime detection ─────────────────────────────────────────
  useEffect(() => {
    if (!promptId || !prompts?.length) {
      setIsPromptRealtime(false);
      return;
    }
    const selectedPrompt = prompts.find((p) => p.id === promptId);
    setIsPromptRealtime(selectedPrompt?.pattern_type === 'realtime');
  }, [promptId, prompts]);

  // ── Fetch agent info ──────────────────────────────────────────────────
  useEffect(() => {
    const f = async () => {
      if (agentId === 'new-agent') return;

      setLoading(true);
      try {
        const [agent, promptsList] = await Promise.all([getAgentById(agentId), getPrompts()]);

        setName(agent.name);
        setPromptId(agent.prompt_id);
        setPrompts(promptsList);
      } catch (err) {
        console.error('Error fetching agent data:', err);
        setError('Failed to load agent data');
      } finally {
        setLoading(false);
      }
    };

    f();
  }, [agentId]);

  // ── Handlers ──────────────────────────────────────────────────────────
  const handlePeriodChange = useCallback((period: { start_date: string; end_date: string }) => {
    setStartDate(period?.start_date);
    setEndDate(period?.end_date);
  }, []);

  const fetchAuditOperations = async (
    skip: number,
    limit: number,
    opId: string | null = null,
    thId: string | null = null,
  ): Promise<void> => {
    if (limit !== itemsPerPage) {
      setItemsPerPage(limit);
    }
    try {
      let start = startDate;
      let end = endDate;

      if (!start) {
        const range = getDateRangeByPeriod('today');
        start = range.start_date;
        end = range.end_date;
      }

      setLoading(true);
      const response = await getAgentAuditOperations(agentId, skip, limit, start, end, opId, thId);
      setAuditOperations(response.items);
      setTotalAuditOperations(response.totalCount);
      setItemsPerPage(limit);
    } catch (err) {
      console.error('Error fetching audit operations:', err);
      setError('Failed to load audit operations');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchByOperationIdChange = (e: React.ChangeEvent<HTMLInputElement>): void =>
    setOperationId(e.target.value);
  const handleSearchByThreadIdChange = (e: React.ChangeEvent<HTMLInputElement>): void =>
    setThreadId(e.target.value);

  const handleRowClick = (row: AssistantAuditOperationApi): void => {
    navigate(`/organizations/${organizationId}/agents-hub/${agentId}/audit-operations/${row.id}`);
  };

  const handleSearchByOperationIdKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      setThreadId('');
      fetchAuditOperations(0, itemsPerPage, operationId);
    }
  };

  const handleSearchByThreadIdKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      setOperationId('');
      fetchAuditOperations(0, itemsPerPage, null, threadId);
    }
  };

  const handleSearchByOperationIdClear = (): void => {
    setOperationId('');
    fetchAuditOperations(0, itemsPerPage, '');
  };

  const handleSearchByThreadIdClear = (): void => {
    setThreadId('');
    fetchAuditOperations(0, itemsPerPage, '');
  };

  useEffect(() => {
    fetchAuditOperations(0, itemsPerPage);
  }, [startDate, endDate]);

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-6">
          <div className="d-flex justify-between align-center">
            <h2 className="h2-title m-0">{name}</h2>

            <div className="buttons-container d-flex justify-end">
              {detailAudit && (
                <Button variant="primary" size="small" onClick={() => setDetailAudit(null)}>
                  Back
                </Button>
              )}

              <Button
                variant="secondary"
                size="large"
                onClick={() => navigate(`/organizations/${organizationId}/agents-hub/${agentId}`)}
              >
                View Settings
              </Button>

              <Button
                variant="secondary"
                size="large"
                onClick={() =>
                  navigate(`/organizations/${organizationId}/agents-hub/${agentId}/instructions`)
                }
              >
                View Instructions
              </Button>
            </div>
          </div>

          <div className="gap d-flex flex-row-to-col flex-column">
            <div className="int-col-3 p-2 border-yes radius-yes full-width-on-sml-scr">
              <div className="radius-yes p-2 bg-yes col-12">
                <div className={'mb-3 p-2'}>
                  <DateRangeSelector
                    storageKey="agent-audit-operations"
                    onChange={handlePeriodChange}
                  />
                  <div style={{ display: 'flex', gap: '10px' }} className="mt-2">
                    <div className="search" style={{ flex: 1 }}>
                      <div className="search-wrapper">
                        <div className="icon-wrapper">
                          <i className="fa-solid fa-magnifying-glass"></i>
                        </div>
                        <input
                          type="text"
                          className="search-input"
                          placeholder="Search by ID..."
                          value={operationId}
                          onChange={handleSearchByOperationIdChange}
                          onKeyDown={handleSearchByOperationIdKeyDown}
                        />
                        {operationId && (
                          <div
                            className="icon-wrapper"
                            style={{ cursor: 'pointer' }}
                            onClick={handleSearchByOperationIdClear}
                          >
                            <i className="fa-solid fa-times"></i>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="search" style={{ flex: 1 }}>
                      <div className="search-wrapper">
                        <div className="icon-wrapper">
                          <i className="fa-solid fa-magnifying-glass"></i>
                        </div>
                        <input
                          type="text"
                          className="search-input"
                          placeholder="Search by Thread ID..."
                          value={threadId}
                          onChange={handleSearchByThreadIdChange}
                          onKeyDown={handleSearchByThreadIdKeyDown}
                        />
                        {threadId && (
                          <div
                            className="icon-wrapper"
                            style={{ cursor: 'pointer' }}
                            onClick={handleSearchByThreadIdClear}
                          >
                            <i className="fa-solid fa-times"></i>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {error ? (
                  <div>Error: {error}</div>
                ) : (
                  <Table
                    title="Audit Operations"
                    columns={columns}
                    data={auditOperations}
                    onRowClicked={handleRowClick}
                    selectableRowsHighlight
                    progressPending={loading}
                    paginationPerPage={itemsPerPage}
                    paginationTotalRows={totalAuditOperations}
                    sortIcon={<i className="fa-solid fa-chevron-down"></i>}
                    onChangePage={fetchAuditOperations}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="radius-yes p-2 bg-yes radius col-6">
          <h2 className="h2-title">Test Agent</h2>
          <div className="gap d-flex border-yes radius-yes flex-row-to-col flex-column border-yes">
            <TestAgentModal isOpen={true} isPromptRealtime={isPromptRealtime} />
          </div>
        </div>
      </div>
    </div>
  );
}

function AgentAuditOperationsList() {
  return (
    <ConfirmationProvider>
      <AgentApiProvider>
        <AgentAuditOperationsListContent />
      </AgentApiProvider>
    </ConfirmationProvider>
  );
}

export default AgentAuditOperationsList;
